import { HStack, UI, Stack } from '@genial/components';
import { BoxRadius, Spacing } from '@genial/design-tokens';
import { Box } from 'native-base';
import React from 'react';

import { InputMoney } from '../../../../../../../../../components/InputMoney';
import {
  TableContent,
  TableContentItemColumn,
  TableContentItemText,
} from '../../../../../../../../../components/TableContent';
import { useGlobalAssetContext } from '../../../../../contexts';
import { useFundsSellAssetController } from '../../../../../hooks/products/FundsSell';
import { useFundsSellTaxSimulationController } from '../../../../../hooks/products/FundsSell/taxSimulation';
import { LateralBar } from '../../../components/LateralBar';
import { fundsSellConstants } from '../constants';
import { ListLoadingItemDesktop } from '../ListLoadingItem/desktop';

import { FundsListItemTypes } from '.';

export const FundsListItemDesktop: React.FC<FundsListItemTypes.Props> = ({ offer }) => {
  const id = offer.productIdFormatted;
  const {
    handleInputFocus,
    handleInputClear,
    isFocused,
    handleInputBlur,
    handleSubmit,
    control,
    hasError,
    helpText,
    currentValue,
    currentValueFormatted,
    isTotalRedemption,
    handleToggleIsTotalRedemption,
    isTotalRedemptionChecked,
  } = useFundsSellAssetController(offer);
  const { infoGetter, selectedTab } = useGlobalAssetContext();

  const completeOffer = { ...offer, orderValue: currentValue, isTotalRedemption };

  const { isLoadingTaxes, taxSimulationResponse } = useFundsSellTaxSimulationController({
    valueToSimulate: currentValue,
    product: completeOffer,
  });

  const { Button, isSelected, forceLoading } = infoGetter(
    'fundsSell',
    completeOffer,
    'productIdNormalized'
  );

  const getFlex = (index: number) => {
    const currentHead = fundsSellConstants.tableHead.find((_, i) => i === index);
    return currentHead?.flex || 1;
  };

  if (forceLoading) {
    return <ListLoadingItemDesktop />;
  }

  return (
    <TableContent
      direction="row"
      space={0}
      p={0}
      px={Spacing['inline.xxxs']}
      h="80px"
      isHighlighted={isSelected}>
      <LateralBar
        backgroundColor={selectedTab.color}
        borderTopLeftRadius={BoxRadius.sm}
        borderBottomLeftRadius={BoxRadius.sm}
      />
      <TableContentItemColumn flex={getFlex(0)}>
        <UI.Tooltip testID={`fund-productName-tooltip-${id}`} label={offer.productName}>
          <TableContentItemText>{offer.productName}</TableContentItemText>
        </UI.Tooltip>
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(1)}>
        <UI.Switch onToggle={handleToggleIsTotalRedemption} isChecked={isTotalRedemptionChecked} />
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(2)}>
        <Stack pr={Spacing['inline.xxxs']}>
          <InputMoney
            id={`funds-sell-input${id}`}
            name="redemptionValue"
            control={control}
            handleFocus={handleInputFocus}
            handleBlur={handleInputBlur}
            handleSubmit={handleSubmit}
            handleClear={handleInputClear}
            currentValue={currentValueFormatted}
            hasFocus={isFocused}
            minValue={offer.minimumTransaction || 0}
            isDisabled={isTotalRedemptionChecked}
            customInputProps={{
              ...{ ...(isFocused && hasError && { error: hasError }) },
              ...{ ...(isFocused && !hasError && { helpText }) },
            }}
          />
        </Stack>
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(3)} pr="inline.nano">
        <UI.Text isLoading={isLoadingTaxes}>
          {taxSimulationResponse?.irValueNormalized || offer.estimatedIncomeTax}
        </UI.Text>
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(4)} pr="inline.nano">
        <UI.Text isLoading={isLoadingTaxes}>
          {taxSimulationResponse?.liquidityValueNormalized || offer.estimatedNetValue}
        </UI.Text>
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(5)}>
        <HStack space="inline.nano" alignItems="center">
          {!!offer.quotationAndLiquidationNormalized && (
            <UI.Tooltip label={offer.quotationAndLiquidationNormalized} maxW="283px">
              <Stack>
                <UI.Icon name="Info" size="xxs" color="neutral.medium" />
              </Stack>
            </UI.Tooltip>
          )}
          <TableContentItemText>{offer.quotationDateFormatted}</TableContentItemText>
        </HStack>
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(6)}>
        <HStack space="inline.nano" alignItems="center">
          {!!offer.quotationAndLiquidationNormalized && (
            <UI.Tooltip label={offer.quotationAndLiquidationNormalized} maxW="283px">
              <Stack>
                <UI.Icon name="Info" size="xxs" color="neutral.medium" />
              </Stack>
            </UI.Tooltip>
          )}
          <TableContentItemText>{offer.liquidationDateFormatted}</TableContentItemText>
        </HStack>
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(7)}>
        <Box w={'100%'}>
          <Button
            id={selectedTab.typeGetterId}
            isDisabled={typeof hasError?.message === 'string' || isLoadingTaxes}
            offer={completeOffer}
            productContextConstant={selectedTab}
          />
        </Box>
      </TableContentItemColumn>
    </TableContent>
  );
};
